/**
 * Réglages › Portails: the gates a pass can be scanned at.
 *
 * The list and its « Ajouter » form were the original /reglages page's own
 * body, above the six sections that had nothing to do with them.
 */
import { useState } from "react";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { DoorOpen, Plus } from "lucide-react";

import { Button } from "@gately/ui/button";
import { EmptyState } from "@gately/ui/empty-state";
import { Input } from "@gately/ui/input";
import { SettingsSection } from "@gately/ui/settings";
import { toast } from "@gately/ui/toast";

import { orpc } from "~/lib/orpc";

export const Route = createFileRoute("/_dashboard/reglages/portails")({
  // Prefetch so useSuspenseQuery has data ready and never suspends mid-navigation.
  // `me.get` is not listed: nothing on this tab reads it, and the layout's
  // beforeLoad has already awaited it by the time this runs.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(orpc.gate.list.queryOptions()),
  component: PortailsTab,
});

function PortailsTab() {
  const queryClient = useQueryClient();
  const { data: gates } = useSuspenseQuery(orpc.gate.list.queryOptions());

  return (
    <SettingsSection title="Portails">
      <div className="divide-y">
        {gates.length === 0 ? (
          <EmptyState
            title="Aucun portail."
            description="Ajoutez le premier portail avec le formulaire ci-dessous."
          />
        ) : (
          gates.map((g) => (
            <div key={g.id} className="flex items-center gap-3 p-4">
              <DoorOpen className="text-muted-foreground size-4" />
              <span className="font-medium">{g.name}</span>
            </div>
          ))
        )}
      </div>

      <GateForm queryClient={queryClient} />
    </SettingsSection>
  );
}

function GateForm({
  queryClient,
}: {
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [name, setName] = useState("");

  const createGate = useMutation(
    orpc.gate.create.mutationOptions({
      onSuccess: () => {
        setName("");
        toast.success("Portail ajouté.");
        void queryClient.invalidateQueries({ queryKey: orpc.gate.list.key() });
      },
      onError: (err) => toast.error(err.message),
    }),
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    createGate.mutate({ name: trimmed });
  };

  return (
    <form onSubmit={submit} className="flex gap-2">
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Nom du portail (ex. Portail piscine)"
        maxLength={80}
      />
      <Button type="submit" disabled={createGate.isPending || !name.trim()}>
        <Plus className="size-4" />
        Ajouter
      </Button>
    </form>
  );
}
